import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { bookUrl } from "../../lib/api";

/**
 * BookshelfSection — 3D wooden shelf with navy-and-gold spines.
 *
 * Visual language (intentional, NOT negotiable per the user spec):
 *   • Every spine is navy-on-gold — same palette as the existing
 *     "תורה" cover and the in-reader paper. A red/green/purple spine
 *     would break the hand-off illusion (spine → cover → reader).
 *   • Spines are CSS-only 3D (perspective + transform). No three.js,
 *     no extra JS — mobile-safe.
 *   • Each spine has a small height/thickness variation so the row
 *     looks like a real bookshelf, not a tile grid.
 *
 * Shelves currently rendered through this component:
 *   • תורה        — 5 books
 *   • נביאים      — 21 books (overflows → horizontal scroll on most
 *                    screens; the swipe is contained to the shelf row
 *                    via `touch-action: pan-x` so it can't get into
 *                    a fight with the page-level vertical scroll on
 *                    Android Chrome PWA — same class of bug we hit
 *                    earlier with `overflow-x: hidden` on `<html>`).
 *   • כתובים      — 13 books
 *   • תפילה       — 1 book (the Siddur)
 *
 * Reading flow stays UNCHANGED — every spine routes via the same
 * URL the legacy cards used. Tanakh spines go to `/reader/{slug}/1`;
 * the Siddur spine uses an explicit `book.route` (e.g. "/siddur").
 */

// Deterministic spine sizing — keyed by slug so a book always renders
// the same height/thickness across refreshes. Sized to look believable
// on a real shelf: small books (12 prophets, megillot, Siddur) are
// narrower & a touch shorter; large books (Yeshayahu, Yirmiyahu,
// Tehillim, the Torah chumashim) are taller & thicker.
//
// `shade` is 0/1/2 — picks one of the 3 navy gradients below.
const VARIATIONS = {
  // ── Torah ─────────────────────────────────────────────────
  Genesis:       { height: 292, thickness: 38, shade: 0 },
  Exodus:        { height: 285, thickness: 36, shade: 1 },
  Leviticus:     { height: 270, thickness: 32, shade: 2 },
  Numbers:       { height: 295, thickness: 40, shade: 0 },
  Deuteronomy:   { height: 300, thickness: 42, shade: 1 },

  // ── Nevi'im ───────────────────────────────────────────────
  Joshua:        { height: 268, thickness: 30, shade: 2 },
  Judges:        { height: 274, thickness: 32, shade: 0 },
  "I Samuel":    { height: 282, thickness: 34, shade: 1 },
  "II Samuel":   { height: 280, thickness: 32, shade: 2 },
  "I Kings":     { height: 286, thickness: 34, shade: 0 },
  "II Kings":    { height: 284, thickness: 32, shade: 1 },
  Isaiah:        { height: 305, thickness: 42, shade: 2 },
  Jeremiah:      { height: 305, thickness: 44, shade: 0 },
  Ezekiel:       { height: 298, thickness: 38, shade: 1 },
  // Trei Asar — twelve small prophets, all narrower
  Hosea:         { height: 250, thickness: 22, shade: 2 },
  Joel:          { height: 240, thickness: 18, shade: 0 },
  Amos:          { height: 250, thickness: 22, shade: 1 },
  Obadiah:       { height: 232, thickness: 16, shade: 2 },
  Jonah:         { height: 236, thickness: 18, shade: 0 },
  Micah:         { height: 248, thickness: 22, shade: 1 },
  Nahum:         { height: 240, thickness: 18, shade: 2 },
  Habakkuk:      { height: 240, thickness: 18, shade: 0 },
  Zephaniah:     { height: 242, thickness: 18, shade: 1 },
  Haggai:        { height: 232, thickness: 16, shade: 2 },
  Zechariah:     { height: 256, thickness: 24, shade: 0 },
  Malachi:       { height: 238, thickness: 18, shade: 1 },

  // ── Ketuvim ───────────────────────────────────────────────
  Psalms:        { height: 308, thickness: 44, shade: 0 },
  Proverbs:      { height: 280, thickness: 32, shade: 1 },
  Job:           { height: 275, thickness: 32, shade: 2 },
  // 5 Megillot — petite
  "Song of Songs": { height: 244, thickness: 20, shade: 0 },
  Ruth:          { height: 236, thickness: 18, shade: 1 },
  Lamentations:  { height: 240, thickness: 18, shade: 2 },
  Ecclesiastes:  { height: 250, thickness: 22, shade: 0 },
  Esther:        { height: 248, thickness: 22, shade: 1 },
  // Late Ketuvim
  Daniel:        { height: 270, thickness: 28, shade: 2 },
  Ezra:          { height: 264, thickness: 26, shade: 0 },
  Nehemiah:      { height: 268, thickness: 28, shade: 1 },
  "I Chronicles":  { height: 292, thickness: 36, shade: 2 },
  "II Chronicles": { height: 294, thickness: 38, shade: 0 },

  // ── Tefilah ───────────────────────────────────────────────
  // Siddur — tall and substantial, sits alone on its shelf.
  siddur:        { height: 310, thickness: 50, shade: 1 },

  // ── Mishnah ───────────────────────────────────────────────
  // Each tractate is its own bound volume. Sized in a range
  // believable for a Mishnah set (slightly shorter than the
  // Chumashim, similar thickness). When more tractates land
  // we can hand-tune them per their actual length.
  "mishnah-berakhot": { height: 286, thickness: 36, shade: 1 },

  // ── Talmud Bavli ──────────────────────────────────────────
  // A Bavli tractate is much thicker than its Mishnah counterpart
  // (a full Vilna folio runs hundreds of pages vs. the Mishnah's
  // dozens). Render them taller AND thicker so the user feels the
  // weight at a glance. Sizes scale roughly with daf count:
  //   Berakhot ~64 dafim → 56px / 308h
  //   Shabbat  ~157 dafim → 68px / 318h   (largest in Seder Moed)
  "berakhot-bavli":      { height: 308, thickness: 56, shade: 0 },
  "shabbat-bavli":       { height: 318, thickness: 68, shade: 1 },
  // Seder Mo'ed (Bavli) — sized by daf-count tier:
  //   120+ dafim → 320h / 72px   (Pesachim)
  //   100+ dafim → 316h / 66px   (Eruvin)
  //    80+ dafim → 312h / 62px   (Yoma)
  //    50+ dafim → 306h / 52px   (Sukkah)
  //    30–40   → 300–302h / 44–46px
  "pesachim-bavli":      { height: 320, thickness: 72, shade: 0 },
  "eruvin-bavli":        { height: 316, thickness: 66, shade: 2 },
  "yoma-bavli":          { height: 312, thickness: 62, shade: 1 },
  "sukkah-bavli":        { height: 306, thickness: 52, shade: 2 },
  "beitzah-bavli":       { height: 302, thickness: 46, shade: 0 },
  "rosh-hashanah-bavli": { height: 300, thickness: 44, shade: 1 },
  "taanit-bavli":        { height: 298, thickness: 42, shade: 2 },
  "megillah-bavli":      { height: 300, thickness: 44, shade: 0 },
  "moed-katan-bavli":    { height: 298, thickness: 42, shade: 1 },
  "chagigah-bavli":      { height: 298, thickness: 42, shade: 2 },
};

// 3 vivid teal-green shades for Mishnah spines, modelled on the
// classical bound Mishnah volumes (Vilna / Artscroll Mishnayot binding).
// Hue ~175° with high saturation so they read as ירוק-טורקיז חי against
// the cream shelf, distinctly different from the navy Tanakh spines —
// the earlier dark-teal palette (#15414c / #1a4d59) was technically
// teal but too dark/desaturated to be visually distinguishable on a
// small spine. These values are noticeably greener and more saturated.
const TEAL_SHADES = [
  { from: "#1e6b65", to: "#0a3530" },  // base (classical Mishnah teal)
  { from: "#23807a", to: "#0d3f39" },  // a touch brighter
  { from: "#175851", to: "#072824" },  // a touch deeper
];

// 3 oxblood/dark-gold shades for Talmud Bavli spines, modelled on the
// classical Vilna Shas binding. Hue ~22° (warm brown) — visually
// distinct from both the cool Tanakh navy and the Mishnah teal so a
// shelf of mixed corpora reads at a glance.
const TALMUD_SHADES = [
  { from: "#3d2415", to: "#1a0e07" },
  { from: "#4a2c19", to: "#21130a" },
  { from: "#311c10", to: "#150905" },
];

// 3 navy shades the spines cycle through — all in the navy-gold family
// per the user's strict color rule. Difference is subtle (gradient stop
// brightness) so the shelf feels alive without breaking the palette.
const NAVY_SHADES = [
  { from: "#1a2b4a", to: "#0f1a30" },  // base
  { from: "#1e3253", to: "#13213a" },  // a touch brighter
  { from: "#162641", to: "#0b162a" },  // a touch deeper
];

// Hash-based fallback for any book we haven't sized explicitly (so the
// shelf doesn't all become identical 280×36 grey blocks if a new book
// shows up before we hand-tune it).
function fallbackVariation(slug, index) {
  const seed = Array.from(String(slug)).reduce((a, c) => a + c.charCodeAt(0), index);
  return {
    height: 260 + (seed % 5) * 8,        // 260, 268, 276, 284, 292
    thickness: 26 + (seed % 4) * 4,      // 26, 30, 34, 38
    shade: seed % NAVY_SHADES.length,
  };
}


export default function BookshelfSection({ section }) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  // Mishnah shelves use the teal-gold palette of classical bound
  // Mishnah volumes; Talmud Bavli shelves use the oxblood/dark-gold
  // of bound Vilna Shas; everything else (Torah, Nevi'im, Ketuvim,
  // Tefilah) stays on the original navy-gold palette. Mishnah section
  // keys are `mishnah-zeraim`, `mishnah-moed`, etc. (one shelf per seder).
  const isMishnah = section.key === "mishnah" || section.key?.startsWith("mishnah-");
  const isTalmud = section.key === "talmud-bavli" || section.key?.startsWith("talmud-");
  const palette = isTalmud ? "talmud" : isMishnah ? "teal" : "navy";
  // Header text resolution. EN mode promotes `section.en` (Sefaria's
  // canonical English name — "Torah", "Nevi'im (Prophets)", "Mishnah ·
  // Mo'ed (Festivals)") to the headline and demotes the Hebrew to the
  // italic secondary line. HE mode is byte-identical to legacy: Hebrew
  // headline, English italic. This is a pure UI flip — no data changes.
  const primary   = isEn ? (section.en || section.he) : section.he;
  const secondary = isEn ? section.he : section.en;
  return (
    <section
      data-testid={`bookshelf-section-${section.key}`}
      className="mb-12 sm:mb-16 animate-fade-in-up"
    >
      <header className="flex items-baseline justify-between mb-3 sm:mb-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-foreground">
            {primary}
          </h2>
          {secondary && (
            <span className="font-latin italic text-xs sm:text-sm text-muted-foreground mt-1 block">
              {secondary}
            </span>
          )}
        </div>
        <span className="font-heading text-xs sm:text-sm text-gold">
          {t("library_page.books_count", { count: section.books.length })}
        </span>
      </header>

      <div className="gold-rule mb-4 sm:mb-5" />

      <div
        className="bookshelf-frame"
        data-testid={`bookshelf-${section.key}`}
      >
        {/* The row of spines.
            On large shelves (Nevi'im, Ketuvim) this overflows
            horizontally — `.bookshelf-row` declares `touch-action:
            pan-x` so the gesture is bound to horizontal scroll within
            the row and never competes with the page's vertical scroll
            on Android Chrome PWA. */}
        <div className="bookshelf-row" dir="rtl">
          {section.books.map((book, i) => (
            <BookSpine key={book.slug} book={book} index={i} palette={palette} />
          ))}
        </div>
        <div className="bookshelf-plank" aria-hidden="true" />
        <div className="bookshelf-plank-shadow" aria-hidden="true" />
      </div>
    </section>
  );
}


function BookSpine({ book, index, palette = "navy" }) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const v = VARIATIONS[book.slug] || fallbackVariation(book.slug, index);
  const shades = palette === "teal"
    ? TEAL_SHADES
    : palette === "talmud"
      ? TALMUD_SHADES
      : NAVY_SHADES;
  const tone = shades[v.shade % shades.length];

  // Spine label resolution:
  //   • HE mode → book.he (e.g. "בראשית")           ← legacy, untouched
  //   • EN mode → book.en (e.g. "Genesis") with a
  //               graceful fallback to book.slug if
  //               the backend hasn't surfaced an en.
  // Bavli tractate slugs end in "-bavli" (e.g. "berakhot-bavli"); the
  // backend already strips that suffix in `book.en` ("Berakhot"), so
  // no further trimming is needed here.
  const spineLabel = isEn ? (book.en || book.slug) : book.he;

  // Inline custom-props let each spine be styled in CSS without
  // ballooning the class set. The CSS vars are palette-agnostic
  // (named `--spine-navy-*` for historical reasons) — the values
  // simply change per palette.
  //
  // EN labels are Latin script. On a vertical spine the Latin glyphs
  // need a tighter letter-spacing AND a slightly smaller font-size to
  // avoid clipping the cap caps for long names like "Deuteronomy" or
  // "II Chronicles". We pass a writing-mode override only in EN mode;
  // HE mode renders exactly as before.
  const style = {
    "--spine-height": `${v.height}px`,
    "--spine-thickness": `${v.thickness}px`,
    "--spine-navy-from": tone.from,
    "--spine-navy-to": tone.to,
  };

  // Most books route to /reader/{slug}/1. The synthetic Siddur on the
  // Tefilah shelf carries an explicit `route` so it lands on its own
  // page.
  const to = book.route || `/reader/${bookUrl(book.slug)}/1`;

  return (
    <Link
      to={to}
      aria-label={t("library_page.open_book_aria", { name: spineLabel })}
      data-testid={`book-spine-${book.slug.replace(/ /g, "-")}`}
      className={`book-spine ${isEn ? "book-spine--en" : ""}`}
      style={style}
    >
      <span className="book-spine-inner">
        <span className="book-spine-cap-top" aria-hidden="true" />
        <span
          className="book-spine-title"
          data-testid={`spine-title-${book.slug.replace(/ /g, "-")}`}
          lang={isEn ? "en" : "he"}
        >
          {spineLabel}
        </span>
        <span className="book-spine-cap-bottom" aria-hidden="true" />
        <span className="book-spine-side" aria-hidden="true" />
      </span>
    </Link>
  );
}
